import { useState, useEffect, useCallback } from 'react';
import { useCheckoutApi } from '@app/react-common/api/useCheckoutApi';
import moment from 'moment';

// API Response interface (matches backend response)
interface CheckoutGroupMetricsApiResponse {
  numberOfCheckouts: number;
  percentageOfAllCheckoutGroups: number;
  numberOfOrders: number;
  checkoutGroupConversionRate: number;
  revenue: number;
  averageOrderValue: number;
  averageCheckoutRevenue: number;
  averageShippingRevenue: number;
}

// UI interface (used by components)
export interface CheckoutGroupMetricsResponse {
  checkouts: number;
  percentageOfAllCheckouts: number;
  orders: number;
  checkoutConversionRate: number;
  revenue: number;
  averageOrderValue: number;
  avgRevenuePerCheckout: number;
  avgShippingRevenue: number;
}

// Status types for metrics fetch
export type MetricsStatus = 'loading' | 'success' | 'no_data' | 'error';

// Map API response to UI interface
const mapApiResponseToMetrics = (apiResponse: CheckoutGroupMetricsApiResponse): CheckoutGroupMetricsResponse => {
  return {
    checkouts: apiResponse.numberOfCheckouts ?? 0,
    percentageOfAllCheckouts: apiResponse.percentageOfAllCheckoutGroups ?? 0,
    orders: apiResponse.numberOfOrders ?? 0,
    checkoutConversionRate: apiResponse.checkoutGroupConversionRate ?? 0,
    revenue: apiResponse.revenue ?? 0,
    averageOrderValue: apiResponse.averageOrderValue ?? 0,
    avgRevenuePerCheckout: apiResponse.averageCheckoutRevenue ?? 0,
    avgShippingRevenue: apiResponse.averageShippingRevenue ?? 0,
  };
};

interface UseCheckoutGroupMetricsProps {
  checkoutGroupId: number;
  startDate?: string;
  endDate?: string;
}

export const useCheckoutGroupMetrics = ({ checkoutGroupId, startDate, endDate }: UseCheckoutGroupMetricsProps) => {
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<CheckoutGroupMetricsResponse | null>(null);
  const [status, setStatus] = useState<MetricsStatus>('loading');

  const failureHandler = useCallback((response: any, statusCode: number) => {
    console.error('Checkout group metrics API error:', response?.message, statusCode);
  }, []);

  const checkoutApi = useCheckoutApi(failureHandler);

  useEffect(() => {
    // the date picker sends the all time range once the shop loads
    if (!startDate || !endDate) return;
    const fromDate = moment(startDate).format('YYYY-MM-DD');
    const toDate = moment(endDate).format('YYYY-MM-DD');
    const fetchMetrics = async () => {
      setLoading(true);
      setStatus('loading');
      try {
        const response = await checkoutApi.get(
          `/admin/v1/metric/checkout-group-data?from_date=${fromDate}&to_date=${toDate}&checkout_group_id=${checkoutGroupId}`,
        );

        // Check HTTP status code
        if (response.status === 204) {
          // No data available for the selected time range
          setMetrics(null);
          setStatus('no_data');
        } else if (response.status === 200) {
          if (response.data === null || response.data === undefined || typeof response.data !== 'object') {
            console.warn(
              'Checkout group metrics API error: Backend returned null or invalid metrics data',
              response.data,
            );
            setMetrics(null);
            setStatus('no_data');
          } else {
            // Map API response to UI format with defaults for missing fields
            const mappedMetrics = mapApiResponseToMetrics(response.data);
            setMetrics(mappedMetrics);
            setStatus('success');
          }
        } else {
          // Unexpected status code
          setMetrics(null);
          setStatus('error');
        }
      } catch (error) {
        console.error('Checkout group metrics API error: Error fetching checkout group metrics:', error);
        setMetrics(null);
        setStatus('error');
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, [checkoutGroupId, startDate, endDate, checkoutApi]);

  return {
    metrics,
    loading,
    status,
  };
};
